<template>
 
  <div>

    <div v-if="pending > 0" class="loading_page">
        <orbit-spinner :animation-duration="1000" :size="100" :color="'#0e96b1'" />
    </div>

    <toggle-collapse v-if="config.dataSources.filter((ds)=> ds.type == 'dynamic').length > 0">
      <component 
        v-for="dataSource in config.dataSources.filter((ds)=> ds.type == 'dynamic')"
        :is='dataSource.component'
        :key="dataSource.name"
        :config='dataSource.config'
        @updateRequest='(change)=> request[change.key] = change.value'
        @updateData='(newData) => {
          $set(formData, dataSource.name, newData)
        }'
      />
    </toggle-collapse>

    <b-card style="position: relative">

      <b-form @submit.prevent="getReport">

        <b-row class="mt-2" v-for="param in config.parameters" :key="param.id">
          <component 
            :is='param.type'
            :config='param.config'
            :errors="errors"
            :data="formData"
            :request="request"
            @updateRequest='(change)=> request[change.key] = change.value'
          />
        </b-row>

        <b-row class="mt-2">
          <!-- reset and submit -->
          <b-col cols="12">
            <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" type="submit" variant="primary" class="mr-1">
              {{ $t('Get Report') }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <b-card v-if="reportData">
          <b-row>

              <b-col cols="3" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
                <label>{{ $t('Show') }}</label>
                <v-select 
                    v-model="request.length"
                    :dir="$store.getters['auth/preferDirIsRtl'] ? 'rtl' : 'ltr'"
                    :options="[5, 10, 25, 50, 100, 500, 1000]" 
                    :clearable="false"
                    v-on:input="getReport"
                    class="per-page-selector d-inline-block mx-50" />
                <label>{{ $t('entries') }}</label>
              </b-col>

              <b-col cols="5"  class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
                <label>{{ $t('Filter') }}</label>
                  <b-input-group class="mx-1">
                    <b-form-input id="filterInput" v-model="reportFilter" type="search" :placeholder="$t('Type to Search')" />
                    <b-input-group-append>
                      <b-button :disabled="!reportFilter" @click="reportFilter = ''">
                        {{ $t('Clear') }}
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
              </b-col>

              <b-col cols="4" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
                  <b-button v-ripple.400="'rgba(255, 255, 255, 0.15)'" @click.prevent="printReport" type="button"
                    variant="primary" class="mx-1">
                    {{ $t('print') }}
                  </b-button>
                  <b-dropdown :text="$t('Export')" offset="25" variant="primary" class="mx-1">
                    <b-dropdown-item
                         v-for="exportable in config.exports" 
                         :key="exportable.label"
                         class="text-center"
                         @click.prevent="() => {
                            downloadReport(exportable.export, exportable.exporter)
                         }"
                    >
                    {{ $t(exportable.label) }}
                  </b-dropdown-item>
                  </b-dropdown>
              </b-col>

              <b-col cols="12" class="mt-1">
                  <component
                    :is="config.table.type + 'Table'"
                    :data="reportData"
                    :config="config.table.config" 
                    :request="request"
                    @updateRequest='(change)=> request[change.key] = change.value'
                    :errors="errors"
                    :pending="pending"
                    @getReport="getReport" 
                  />

                  <div class="mx-4 mb-2">
                    <b-row>

                      <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
                        <span class="text-muted">{{ $t('Showing') }} {{ reportFrom }} {{ $t('to') }} {{ reportTo }} {{ $t('of') }} {{ reportTotal }}
                          {{ $t('entries') }}</span>
                      </b-col>

                      <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
                        <b-pagination 
                            v-model="request.page" 
                            :total-rows="reportTotal" 
                            :per-page="request.length" 
                            v-on:input="getReport"
                            first-number 
                            last-number 
                            class="mb-0 mt-1 mt-sm-0" 
                            prev-class="prev-item" 
                            next-class="next-item"
                        >
                          <template #prev-text>
                            <feather-icon icon="ChevronLeftIcon" size="18" />
                          </template>
                          <template #next-text>
                            <feather-icon icon="ChevronRightIcon" size="18" />
                          </template>
                        </b-pagination>
                      </b-col>

                    </b-row>
                  </div>
              </b-col>
        </b-row>
    </b-card>
  </div>

</template>

<script>

import { OrbitSpinner } from 'epic-spinners'
import axios from '@/libs/axios'
import ToggleCollapse from '@/views/ui/toggle-collapse/ToggleCollapse.vue'
import vSelect from 'vue-select'

import Ripple from 'vue-ripple-directive'
import { ref, computed } from '@vue/composition-api'
import { BButton, BCard, BCol, BDropdown, BDropdownItem, BForm, BFormGroup, BFormInput, BInputGroup, BInputGroupAppend, BPagination, BRow } from 'bootstrap-vue'
import moment from 'moment'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

// data sources
import DeviceDataSource from './DataSources/DeviceDataSource.vue'

// fields
import stmSelect from './Fields/stmSelect.vue'
import dateTimeRange from './Fields/dateTimeRange.vue'


// tables
import defaultTable from './Tables/defaultTable.vue'


export default {
  components: {
    OrbitSpinner,
    ToggleCollapse,
    vSelect,
    DeviceDataSource,
    BCard,
    BForm,
    BFormInput,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
    BDropdown,
    BDropdownItem,
    BPagination,
    FeatherIcon,
    BRow,
    BCol,
    BButton,
    stmSelect,
    dateTimeRange,
    defaultTable
  },
  directives: { Ripple },
  props: {
    config:{
      type:Object
    }
  },
  setup(props, { root }) {

    const pending = ref(0)
    const errors = ref({})
    const request = ref({ 
      type: props.config.type,
      page:1,
      length:25 
    })

    let initFormData = {}
    props.config.dataSources.map((ds) => {
      initFormData[ds.name] = ds.type == 'static' ? ds.data : []
    })

    const formData = ref(initFormData)

    const reportFilter = ref('')
    const reportData = ref(null)
    const reportFrom = ref(0)
    const reportTo = ref(0)
    const reportTotal = ref(0)

    const getReport = () => {
      pending.value++;
      errors.value = {}
      axios.post('reports', request.value)
      .then(res => {
        reportData.value = res.data.data.data
        reportFrom.value = res.data.data.from ?? 0
        reportTo.value = res.data.data.to ?? 0
        reportTotal.value = res.data.data.total
      })
      .catch( err => {
        if(err.response.status == 422){
          errors.value = err.response.data.errors
        }
      })
      .finally(()=> pending.value--)
    }

    const exportReport = (exportType, exporter) => {
      return new Promise((resolve,reject) => {
          axios.post('export', {export:exportType, exporter,...request.value}, {responseType: 'blob'})
          .then( res => {
              resolve(new Blob([res.data], { type: res.headers['content-type'], encoding: 'UTF-8' }))
          })
          .catch( err => reject(err))
      })
    }

    const printReport = () => {
      pending.value++
        exportReport( props.config.type + 'Report', 'pdf')
        .then((blob)=> {
            let iframe = document.createElement('iframe')
            document.body.appendChild(iframe);
            iframe.style.display = 'none';
            iframe.src = URL.createObjectURL(blob);
            iframe.onload = function() {
              setTimeout(function() {
                iframe.focus();
                iframe.contentWindow.print();
              }, 1);
            };
        }).finally(()=> pending.value-- )
    }

    const downloadReport = (exportType, exporter)=> {
        pending.value++
        exportReport(exportType, exporter)
        .then((blob)=> {
          let link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = 'file-' + moment().format('YYYY-MM-DD HH:mm:ss') + '.' + exporter
          link.click()
        }).finally(()=> pending.value-- )
    }
    
    return {
      pending,
      request,
      errors,
      formData,
      reportData,
      reportFrom,
      reportTo,
      reportTotal,
      getReport,
      printReport,
      downloadReport,
      reportFilter,
      log:console.log
    }

  },



}
</script>

<style lang="scss">
  @import '@core/scss/vue/libs/vue-select.scss';
  @import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>